import { TFunction } from 'i18next';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, HStack, Input, Spinner, Text, VStack } from '@bonusly/design-system';

import { useBizyProfile } from '../../queries/bizy-profile';
import { useBizySettingsDraftStore } from './store/use-bizy-settings-draft-store';

// ─── Field labels + placeholders ────────────────────────────────────────────
//
// Known server field names get curated labels and hint placeholders. Any new
// field the server adds is rendered with a snake_case → Title Case label and
// a generic placeholder so no FE change is required when `PROFILE_FIELDS`
// grows.

interface FieldCopy {
	label: string;
	placeholder: string;
}

const titleCase = (snakeCase: string): string =>
	snakeCase
		.split('_')
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');

const copyFor = (t: TFunction<'bizy_settings'>, fieldName: string): FieldCopy => {
	const knownFieldKey = `profile.fields.${fieldName}`;
	const label = t(`${knownFieldKey}.label`, { defaultValue: titleCase(fieldName) });
	const placeholder = t(`${knownFieldKey}.placeholder`, { defaultValue: t('profile.genericPlaceholder') });
	return { label, placeholder };
};

// ─── Profile Field Row ──────────────────────────────────────────────────────

interface ProfileFieldRowProps {
	fieldName: string;
	value: string | null;
}

export const ProfileFieldRow: FC<ProfileFieldRowProps> = ({ fieldName, value }) => {
	const { t } = useTranslation('bizy_settings');
	const draft = useBizySettingsDraftStore((state) => state.pendingProfileFields[fieldName]);
	const setPendingProfileField = useBizySettingsDraftStore((state) => state.setPendingProfileField);
	const clearPendingProfileField = useBizySettingsDraftStore((state) => state.clearPendingProfileField);

	const { label, placeholder } = copyFor(t, fieldName);
	const serverValue = value ?? '';
	const displayValue = draft !== undefined ? draft : serverValue;

	const handleChange = (nextValue: string) => {
		if (nextValue === serverValue) {
			clearPendingProfileField(fieldName);
		} else {
			setPendingProfileField(fieldName, nextValue);
		}
	};

	return (
		<Box>
			<Text size="sm" variant="medium" color="text.soft" mb={2}>
				{label}
			</Text>
			<Input
				value={displayValue}
				onChange={(event) => handleChange(event.target.value)}
				placeholder={placeholder}
				size="md"
				borderRadius="lg"
				bg="bg.surface"
			/>
		</Box>
	);
};

// ─── Profile Section ────────────────────────────────────────────────────────

export const ProfileSection: FC = () => {
	const { t } = useTranslation('bizy_settings');
	const { data: profile, isLoading } = useBizyProfile();

	if (isLoading) {
		return (
			<HStack justify="center" py={4}>
				<Spinner size="sm" />
			</HStack>
		);
	}

	if (!profile) {
		return (
			<Text size="md" color="text.softer">
				{t('profile.loadError')}
			</Text>
		);
	}

	return (
		<VStack spacing={4} align="stretch" pt={3}>
			{profile.required_fields.map((fieldName) => (
				<ProfileFieldRow key={fieldName} fieldName={fieldName} value={profile.fields[fieldName] ?? null} />
			))}
		</VStack>
	);
};
